import { useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'

/**
 * Hook to track user online presence via heartbeat.
 * Sets user as "online" in user_presence table and sends periodic heartbeats.
 * Automatically marks user as "offline" on page close/unmount.
 */
export default function usePresence(userId) {
  const heartbeatRef = useRef(null)

  useEffect(() => {
    if (!userId) return

    const sendHeartbeat = async () => {
      try {
        // Use RPC function for atomic heartbeat update (upserts online status + heartbeat_at)
        await supabase.rpc('update_heartbeat')
      } catch {
        // Fallback: direct upsert if RPC doesn't exist
        try {
          await supabase
            .from('user_presence')
            .upsert({
              user_id: userId,
              status: 'online',
              last_seen: new Date().toISOString(),
              heartbeat_at: new Date().toISOString()
            })
        } catch (fallbackErr) {
          console.error('Presence upsert error:', fallbackErr)
        }
      }
    }

    const setOffline = async () => {
      try {
        await supabase
          .from('user_presence')
          .update({
            status: 'offline',
            last_seen: new Date().toISOString()
          })
          .eq('user_id', userId)
      } catch {
        // Ignore errors during cleanup
      }
    }

    // Set online immediately
    sendHeartbeat()

    // Send heartbeat every 15 seconds
    heartbeatRef.current = setInterval(sendHeartbeat, 15000)

    // Handle page visibility changes — re-send heartbeat when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat()
      }
    }

    // Handle page close — try to set offline
    const handleBeforeUnload = () => {
      // Use synchronous approach for page unload
      const url = `${supabase.supabaseUrl || supabase.restUrl?.replace('/rest/v1', '')}/rest/v1/user_presence?user_id=eq.${userId}`
      const body = JSON.stringify({
        status: 'offline',
        last_seen: new Date().toISOString()
      })
      // navigator.sendBeacon doesn't support custom headers,
      // so use fetch with keepalive which works during unload
      fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabase.supabaseKey,
          'Authorization': `Bearer ${supabase.supabaseKey}`,
          'Prefer': 'return=minimal'
        },
        body,
        keepalive: true
      }).catch(() => {})
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current)
        heartbeatRef.current = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      setOffline()
    }
  }, [userId])
}
